/**
 * CSV/TSV parser with delimiter auto-detection and column mapping (P1.5.6, F-024).
 * No `eval`; numeric validation rejects non-numeric cells (R10).
 */

export interface ParsedCsv {
  delimiter: string;
  headers: string[];
  rows: string[][];
}

/** Auto-detect the delimiter (`,`, `\t`, `;`). */
export function detectDelimiter(text: string): string {
  const candidates = [',', '\t', ';'];
  const firstLine = text.split(/\r?\n/).find((l) => l.trim().length > 0) ?? '';
  let best = ',';
  let bestCount = -1;
  for (const c of candidates) {
    const count = firstLine.split(c).length - 1;
    if (count > bestCount) {
      bestCount = count;
      best = c;
    }
  }
  return best;
}

/**
 * Parse raw CSV/TSV text into headers + rows. Throws on malformed input.
 */
export function parseCsv(text: string, options?: { delimiter?: string }): ParsedCsv {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    throw new Error('File is empty.');
  }
  const delimiter = options?.delimiter ?? detectDelimiter(trimmed);
  const lines = trimmed.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) {
    throw new Error('No rows found in file.');
  }

  const rows = lines.map((line) => line.split(delimiter).map((c) => c.trim()));
  // First row treated as header if it contains any non-numeric cell.
  const firstRow = rows[0]!;
  const firstRowAllNumeric = firstRow.every((c) => c === '' || !Number.isNaN(Number(c)));
  let headers: string[];
  let dataRows: string[][];
  if (!firstRowAllNumeric) {
    headers = firstRow.map((h, i) => h || `column_${i + 1}`);
    dataRows = rows.slice(1);
  } else {
    headers = firstRow.map((_, i) => `column_${i + 1}`);
    dataRows = rows;
  }

  if (dataRows.length === 0) {
    throw new Error('No data rows found (only a header row).');
  }

  return { delimiter, headers, rows: dataRows };
}

export interface ColumnMapping {
  timeColumn: number;
  concentrationColumn: number;
  blqColumn?: number;
}

/**
 * Map columns by header name (case-insensitive). Falls back to positional.
 */
export function inferColumnMapping(headers: string[]): ColumnMapping {
  const lower = headers.map((h) => h.toLowerCase());
  const findIndex = (names: string[]): number => {
    for (const n of names) {
      const idx = lower.indexOf(n);
      if (idx >= 0) return idx;
    }
    return -1;
  };

  const timeColumn = findIndex(['time', 't', 'time (h)', 'time(h)']);
  const concentrationColumn = findIndex([
    'concentration',
    'conc',
    'c',
    'concentration (ng/ml)',
    'concentration(ng/ml)',
  ]);
  const blqColumn = findIndex(['blq', 'isblq', 'is_blq', 'lloq flag']);

  return {
    timeColumn: timeColumn >= 0 ? timeColumn : 0,
    concentrationColumn: concentrationColumn >= 0 ? concentrationColumn : 1,
    ...(blqColumn >= 0 ? { blqColumn } : {}),
  };
}

export interface MappedPoints {
  time: number[];
  concentration: number[];
  isBlq: boolean[];
}

/**
 * Apply a column mapping to parsed rows. Throws on non-numeric cells.
 */
export function mapColumns(parsed: ParsedCsv, mapping: ColumnMapping): MappedPoints {
  const time: number[] = [];
  const concentration: number[] = [];
  const isBlq: boolean[] = [];

  for (const row of parsed.rows) {
    const tRaw = row[mapping.timeColumn];
    const cRaw = row[mapping.concentrationColumn];
    if (tRaw === undefined || cRaw === undefined) {
      throw new Error('A row is missing required columns.');
    }
    const t = Number(tRaw);
    const c = Number(cRaw);
    if (Number.isNaN(t) || Number.isNaN(c)) {
      throw new Error(`Non-numeric cell found (time="${tRaw}", concentration="${cRaw}").`);
    }
    time.push(t);
    concentration.push(c);
    let blq = false;
    if (mapping.blqColumn !== undefined) {
      const raw = row[mapping.blqColumn]?.toLowerCase() ?? '';
      blq = raw === 'true' || raw === '1' || raw === 'yes' || raw === 'blq';
    }
    isBlq.push(blq);
  }

  return { time, concentration, isBlq };
}
